import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowUpRight, Settings2 } from 'lucide-react';
import { api } from '../api/client';
import { useSocle } from '../hooks/useSocle';
import { fmtCHF, fmtPct } from '../utils/format';
import { MOIS_LABELS } from '../types';
import type { LigneBudget, SyntheseBudget } from '../types/budget';

/**
 * Budget : la synthèse consolidée, en amont des grilles de détail.
 *
 * Page en **lecture seule** — aucun montant ne se saisit ici. Chaque ligne est
 * agrégée depuis le module qui fait foi (Revenus, Salaires, Loyers, Charges) et
 * renvoie vers lui d'un clic. La structure suit le compte de résultat du plan
 * comptable (revenu brut → EBITDA → EBIT → résultat net), comme la feuille
 * « BU-résumé » du travail de Boza.
 *
 * Le scénario est traversant : le choisir applique les projections de tous les
 * modules à la fois.
 */
export function BudgetPage() {
  const maintenant = new Date();
  const [annee, setAnnee] = useState(maintenant.getFullYear());
  // undefined = socle pas encore connu ; la grille attend avant de charger
  const socleId = useSocle();
  const [scenarioId, setScenarioId] = useState<number | null | undefined>(undefined);
  const [synthese, setSynthese] = useState<SyntheseBudget | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [chargement, setChargement] = useState(true);
  const [detailsOuverts, setDetailsOuverts] = useState(true);

  const charger = useCallback(() => {
    if (scenarioId === undefined) return; // on attend de connaître le socle
    setErreur(null);
    api.get<SyntheseBudget>(`/budget/synthese?annee=${annee}&scenario=${scenarioId ?? 'definitif'}`)
      .then(setSynthese)
      .catch((e) => setErreur(e.message))
      .finally(() => setChargement(false));
  }, [annee, scenarioId]);

  useEffect(() => {
    if (scenarioId === undefined && socleId !== undefined) setScenarioId(socleId);
  }, [socleId, scenarioId]);

  useEffect(() => { charger(); }, [charger]);

  useEffect(() => {
    if (synthese && scenarioId != null && !synthese.scenarios.some((s) => s.id === scenarioId && !s.archive)) {
      setScenarioId(scenarioId === socleId ? null : socleId ?? null);
    }
  }, [synthese, scenarioId, socleId]);

  const scenariosActifs = useMemo(
    () => (synthese?.scenarios ?? []).filter((s) => !s.archive),
    [synthese]
  );
  const variantes = useMemo(() => scenariosActifs.filter((s) => s.id !== socleId), [scenariosActifs, socleId]);
  const scenarioNom = scenarioId != null
    ? scenariosActifs.find((s) => s.id === scenarioId)?.nom ?? null
    : null;

  const moisCourant = annee === maintenant.getFullYear()
    ? maintenant.getMonth() + 1
    : annee < maintenant.getFullYear() ? 13 : 0;
  const classesColonne = (m: number) =>
    m < moisCourant ? ' col-passe' : m === moisCourant ? ' col-courant' : '';

  const lignes = useMemo(
    () => (synthese?.lignes ?? []).filter((l) => detailsOuverts || l.niveau === 0),
    [synthese, detailsOuverts]
  );

  if (chargement) {
    return <div className="flex items-center justify-center" style={{ height: 240 }}><div className="spinner" /></div>;
  }

  const cles = synthese?.cles;

  /** Une charge s'affiche en négatif (elle se retranche du résultat). */
  function valeurAffichee(l: LigneBudget, v: number): number {
    return l.type === 'charge' ? -v : v;
  }

  function renduLigne(l: LigneBudget) {
    const estResultat = l.type === 'resultat';
    const style = estResultat
      ? { fontWeight: 600 as const }
      : l.niveau === 1
        ? { color: 'var(--ink-2)' }
        : undefined;

    return (
      <tr key={l.cle} className={estResultat ? 'ligne-totaux' : l.niveau === 0 ? 'ligne-groupe' : undefined}>
        <td className="cell-nom" style={style}>
          <span style={{ paddingLeft: l.niveau === 1 ? 18 : 0 }}>
            {l.code && <span className="text-xs text-ink-3" style={{ marginRight: 6 }}>{l.code}</span>}
            {l.libelle}
            {l.page && (
              <a
                className="text-xs"
                href={`#${l.page}`}
                title={`Ouvrir le module ${l.page} — c'est là que ces montants se saisissent`}
                style={{ marginLeft: 8, whiteSpace: 'nowrap' }}
              >
                <ArrowUpRight className="icon-sm" />
              </a>
            )}
          </span>
        </td>
        {l.parMois.map((v, i) => {
          const affiche = valeurAffichee(l, v);
          return (
            <td key={i} className={`cell-mois${classesColonne(i + 1)}${v === 0 ? ' cell-vide' : ''}`}>
              <span className="cell-principal" style={affiche < 0 ? { color: 'var(--err)' } : undefined}>
                {v !== 0 ? fmtCHF(affiche) : '—'}
              </span>
            </td>
          );
        })}
        <td className="cell-total">
          <span
            className="cell-principal"
            style={valeurAffichee(l, l.annuel) < 0 ? { color: 'var(--err)' } : undefined}
          >
            {l.annuel !== 0 ? fmtCHF(valeurAffichee(l, l.annuel)) : '—'}
          </span>
        </td>
      </tr>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div className="flex items-center gap-8">
          <h1 className="page-title">Budget {annee}</h1>
          {cles && (
            <span
              className="stat-pill"
              style={{ color: cles.resultatNet >= 0 ? 'var(--ok)' : 'var(--err)' }}
            >
              résultat CHF {fmtCHF(cles.resultatNet)}
            </span>
          )}
          {/* scenarioId nul = cas dégradé (aucun socle défini) : la synthèse
              retombe sur les décisions seules, qui ne font pas un budget */}
          {scenarioId == null
            ? <span className="stat-pill" style={{ color: 'var(--ink-2)' }}
                title="Aucun socle budgétaire défini : lecture des décisions seules, incomplète (les charges projetées manquent). Désigner un socle dans Scénarios.">décisions seules — socle manquant</span>
            : scenarioId === socleId
              ? <span className="stat-pill" style={{ color: 'var(--accent)' }}
                  title="Le budget en l'état : décisions arrêtées plus hypothèses retenues">budget</span>
              : <span className="stat-pill" style={{ color: 'var(--warn)' }}>variante · {scenarioNom}</span>}
        </div>
        <div className="flex gap-8 items-center">
          {/* Les exercices budgétés : 2026 en cours, 2027 en préparation. Une
              année sans données est estimée par annualisation (voir les notes). */}
          <select className="sort-select" value={annee} onChange={(e) => setAnnee(Number(e.target.value))} title="Exercice budgétaire">
            {[2026, 2027].map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
      </div>

      {erreur && <div className="card mb-16 p-16 text-err">{erreur}</div>}

      {cles && (
        <div className="kpi-row mb-16">
          <div className="kpi-card">
            <span className="kpi-card__label">Produits {annee}</span>
            <span className="kpi-card__value">{fmtCHF(cles.produits)}<span className="kpi-card__suffix">CHF</span></span>
          </div>
          <div className="kpi-card">
            <span className="kpi-card__label">Charges {annee}</span>
            <span className="kpi-card__value">{fmtCHF(cles.chargesTotales)}<span className="kpi-card__suffix">CHF</span></span>
          </div>
          <div className="kpi-card">
            <span className="kpi-card__label">Résultat net</span>
            <span
              className="kpi-card__value"
              style={{ color: cles.resultatNet >= 0 ? 'var(--ok)' : 'var(--err)' }}
            >
              {fmtCHF(cles.resultatNet)}<span className="kpi-card__suffix">CHF</span>
            </span>
          </div>
          <div className="kpi-card">
            <span className="kpi-card__label">Marge nette</span>
            <span className="kpi-card__value">
              {cles.margeNette != null ? fmtPct(cles.margeNette) : '—'}
            </span>
          </div>
        </div>
      )}

      <div className="toolbar">
        <div className="selectable-group" title="Niveau de détail des lignes">
          <button className={`selectable${detailsOuverts ? '' : ' active'}`} onClick={() => setDetailsOuverts(false)}>
            Agrégats
          </button>
          <button className={`selectable${detailsOuverts ? ' active' : ''}`} onClick={() => setDetailsOuverts(true)}>
            Détail
          </button>
        </div>
        {/* Pas de lecture « Décidé seulement » ici : sans les hypothèses, les
            charges d'exploitation (toutes projetées) disparaissent et le
            résultat devient trompeusement flatteur (+671'610 constatés le
            19.08.2026). Cette lecture reste disponible dans les grilles de
            détail, où elle se comprend cellule par cellule. */}
        <div className="flex gap-8 items-center" title="Le budget en l'état, ou une variante">
          <div className="selectable-group">
            <button
              className={`selectable${scenarioId != null && scenarioId === socleId ? ' active' : ''}`}
              disabled={socleId == null}
              title="Le budget en l'état : décisions arrêtées plus hypothèses retenues"
              onClick={() => setScenarioId(socleId ?? null)}
            >
              Budget
            </button>
            <button
              className={`selectable${scenarioId != null && scenarioId !== socleId ? ' active' : ''}`}
              disabled={variantes.length === 0}
              title={variantes.length === 0 ? 'Aucune variante — en créer une dans Scénarios' : 'Une variante du budget'}
              onClick={() => setScenarioId(variantes[0]?.id ?? null)}
            >
              Variante
            </button>
          </div>
          {scenarioId != null && scenarioId !== socleId && (
            <select className="sort-select" value={scenarioId} onChange={(e) => setScenarioId(Number(e.target.value))}>
              {variantes.map((s) => <option key={s.id} value={s.id}>{s.nom}</option>)}
            </select>
          )}
          <button
            className="btn btn-icon"
            title="Gérer les scénarios"
            onClick={() => { window.location.hash = 'scenarios'; }}
          >
            <Settings2 className="icon-sm" />
          </button>
        </div>
      </div>

      <div className="grille-container">
        <table className="table table-compact grille-salaires">
          <thead>
            <tr>
              <th className="cell-nom">Compte de résultat</th>
              {MOIS_LABELS.map((label, i) => (
                <th key={label} className={`cell-mois${classesColonne(i + 1)}`}>
                  {label}
                  {i + 1 === moisCourant && <span className="marqueur-courant" title="Mois en cours" />}
                </th>
              ))}
              <th className="cell-total">Total {annee}</th>
            </tr>
          </thead>
          <tbody>
            {lignes.map(renduLigne)}
            {lignes.length === 0 && (
              <tr><td colSpan={14} className="text-ink-3">Aucune donnée pour cette année.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-16 text-xs text-ink-3">
        <p>
          Vue en lecture seule : chaque montant se corrige dans le module qui fait foi — la flèche en fin de
          libellé y renvoie. Les charges s'affichent en négatif (elles se retranchent du résultat).
        </p>
        {(synthese?.notes ?? []).map((n) => <p key={n}>· {n}</p>)}
      </div>
    </div>
  );
}
